import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useDeleteTalentMutation } from '../../reactWrapper/Redux/apiSlice';
import { awaitingInvite } from './ResendInviteButton';

/**
 * Remove a talent an admin created by mistake.
 *
 * Shown under exactly the same condition as Resend invite: an admin made
 * the account and nobody has set a password on it yet. Once a password
 * exists the person has used the account, and deleting it stops being an
 * undo and starts being destruction - so the button disappears and the
 * server refuses as well.
 *
 * Confirms in place rather than with a modal. It is a small action, but it
 * does not come back, so it should not happen on one click.
 */
const DeleteTalentButton = ({ talent, onDeleted }) => {
  const [confirming, setConfirming] = useState(false);
  const [remove, { isLoading }] = useDeleteTalentMutation();

  if (!awaitingInvite(talent)) {
    return null;
  }

  const run = async () => {
    try {
      const out = await remove(talent.id).unwrap();
      toast.success(out?.message || 'Talent deleted');
      onDeleted?.();
    } catch (err) {
      const message =
        (err && err.data && err.data.error && err.data.error.message) ||
        (err && err.data && err.data.message) ||
        'Could not delete this talent';
      toast.error(message);
    } finally {
      setConfirming(false);
    }
  };

  if (confirming) {
    return (
      <span className='inline-flex items-center gap-1 whitespace-nowrap text-xs'>
        <button
          type='button'
          onClick={run}
          disabled={isLoading}
          className='rounded-lg border border-red-300 px-2 py-[1.56px] font-medium text-red-700'
        >
          {isLoading ? 'Deleting…' : 'Delete for good'}
        </button>
        <button
          type='button'
          onClick={() => setConfirming(false)}
          disabled={isLoading}
          className='text-muted-foreground'
        >
          Cancel
        </button>
      </span>
    );
  }

  return (
    <button
      type='button'
      onClick={() => setConfirming(true)}
      className='inline-block whitespace-nowrap rounded-lg border border-border px-[11.7px] py-[1.56px] text-sm text-muted-foreground hover:border-red-300 hover:text-red-700'
      title='Only possible while they have not set a password'
    >
      Delete
    </button>
  );
};

export default DeleteTalentButton;
